import { yupResolver } from '@hookform/resolvers/yup';
import { CheckCircle } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ERROR_MESSAGES, INFO_MESSAGES, SIGN_UP_CONFIRM_URL } from '../../constants/index.js';
import { RegistrationStep } from '../../constants/steps.js';
import { useRegistration } from '../../contexts/RegistrationContext.js';
import { useCustomRegistrationConfig } from '../../hooks/use-custom-registration-config.js';
import { fetchData } from '../../utils/fetch-data.js';
import { confirmationCodeSchema, type ConfirmationCodeFormData } from '../../validation/schemas.js';
import InfoBlock from '../InfoBlock.js';
import OtpInput from '../OtpInput.js';
import { BaseText, ErrorText, PrimaryButton } from '../shared/index.js';

type ConfirmResponse = {
  readonly userCreated?: boolean;
  readonly detail?: string;
};

const ConfirmationCode = () => {
  const {
    confirmationCode,
    completeStep,
    error,
    otpExpiresAt,
    setConfirmationCode,
    setError,
    setIsLoading,
    setUserCreated,
    userInfo,
  } = useRegistration();

  const config = useCustomRegistrationConfig();

  const noZone = otpExpiresAt?.replace(/\[.*]$/, '');
  const formattedDate = noZone ? noZone.replace(/(\.\d{3})\d+Z$/, '$1Z') : '';
  const otpTimestamp = formattedDate
    ? new Date(formattedDate).toLocaleTimeString(navigator.language, {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  const [localError, setLocalError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ConfirmationCodeFormData>({
    resolver: yupResolver(confirmationCodeSchema),
    mode: 'onSubmit',
    defaultValues: { confirmationCode },
  });

  const onSubmit = async (data: ConfirmationCodeFormData) => {
    setLocalError('');
    setError(undefined);
    setIsVerifying(true);
    setIsLoading(true);

    if (!config?.apiUrl) {
      setError('Registration service is not configured.');
      setIsVerifying(false);
      setIsLoading(false);
      return;
    }

    try {
      const apiBaseUrl = config.apiUrl;

      const response = await fetchData(`${apiBaseUrl}${SIGN_UP_CONFIRM_URL}`, 'POST', {
        body: JSON.stringify({ email: userInfo.email, code: data.confirmationCode }),
      });

      const responseData: ConfirmResponse = await response.json();

      if (response.status === 200) {
        setUserCreated(responseData.userCreated ?? false);
        setConfirmationCode(data.confirmationCode);
        completeStep(RegistrationStep.CONFIRMATION_CODE);
      } else {
        throw new Error(responseData.detail);
      }
    } catch (verifyError) {
      setError(verifyError instanceof Error ? verifyError.message : ERROR_MESSAGES.GENERIC_ERROR);
    } finally {
      setIsVerifying(false);
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <InfoBlock email={userInfo.email} />

      <form
        style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          name="confirmationCode"
          control={control}
          render={({ field }) => (
            <Box>
              <Typography sx={{ mb: 1 }} variant="subtitle1">
                Enter Confirmation Code
              </Typography>
              <OtpInput
                fieldNumber={6}
                isError={Boolean(errors.confirmationCode) || Boolean(localError) || Boolean(error)}
                value={field.value}
                onChange={field.onChange}
              />
              {localError && <ErrorText>{localError}</ErrorText>}
              {error && <ErrorText>{error}</ErrorText>}
            </Box>
          )}
        />

        {otpTimestamp && (
          <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'flex-start' }}>
            <BaseText sx={{ fontSize: 14 }}>
              {INFO_MESSAGES.CODE_NOT_RECEIVED} {otpTimestamp}
            </BaseText>
          </Box>
        )}

        <PrimaryButton
          disabled={!isValid || isVerifying}
          endIcon={isVerifying ? <CheckCircle /> : undefined}
          sx={{ mt: 0 }}
          type="submit"
        >
          {isVerifying ? 'Verifying...' : 'Verify Code'}
        </PrimaryButton>
      </form>
    </Box>
  );
};

export default ConfirmationCode;
